/**
 * VIEW-AS (PRO-VIEW-1) — the pro release gate, client half.
 *
 * Peter, 2026-08-29: "I cannot release to them unless I review each one of their account
 * to validate exactly what they will see." An operator opens
 * pro.imageos.co/?viewAs=<proId> SIGNED IN AS THEMSELVES and the Hub renders that pro's
 * screens, read only. Design: dev.cadenceadvisers.com/design/pro-view-as.html (v2).
 *
 * THE SECURITY SHAPE, stated once and leaned on everywhere:
 *   · The viewer stays on THEIR OWN Supabase session. Every read runs under their own
 *     token, through the operator read policies that already exist — view-as grants no
 *     authority the viewer doesn't already have, and RLS itself is the validator: if
 *     they cannot read that pro's row, the mode never engages.
 *   · The app presents the PRO's identity (useSession returns the pro's auth_user_id,
 *     so every page resolves the pro naturally through its normal reads).
 *   · Writes are refused twice: the supabase client blocks every mutation in view-as
 *     (see supabase.ts), and underneath that, pro-owned writes key on the caller's REAL
 *     identity server-side — which has no pro row.
 *
 * The param is adopted into sessionStorage on first load so it survives the login
 * round-trip and in-app navigation; Exit clears it and returns to the real identity.
 */

const KEY = 'hub-view-as-pro';

/** Adopt ?viewAs= from the address into sessionStorage. Runs once at app bootstrap. */
export function adoptViewAsParam(): void {
  try {
    const p = new URLSearchParams(window.location.search).get('viewAs');
    if (p && /^[0-9a-f-]{36}$/.test(p)) sessionStorage.setItem(KEY, p);
  } catch {
    // storage unavailable (private mode edge cases) — view-as simply doesn't engage
  }
}

export function viewAsProId(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function exitViewAs(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.location.assign('/');
}

/** The rpc arg every pro-identity rpc call spreads in — {} when not viewing. */
export function viewRpcArgs(): Record<string, string> {
  const id = viewAsProId();
  return id ? { p_view_pro: id } : {};
}
